import { type VendorCatalogProductDetail } from '@choco-admin/types';
import { useMemo } from 'react';

import { useSuppliersPageVendorItemUnitDetailQuery } from '../../../../../../../generated';
import { mapVendorItemUnitDetailResponse } from '../utils/vendorCatalogDetailMapper';

interface UseVendorItemUnitDetailResult {
  detail: VendorCatalogProductDetail | null;
  isLoading: boolean;
  errorMessage: string | null;
}

/**
 * Loads the full read-only detail of one catalog product by VendorItemUnit id.
 * Pass `null` to skip (e.g. when the detail view is closed).
 */
export const useVendorItemUnitDetail = (
  unitId: string | null
): UseVendorItemUnitDetailResult => {
  const { data, loading, error } = useSuppliersPageVendorItemUnitDetailQuery({
    variables: { ids: unitId ? [unitId] : [] },
    skip: !unitId,
    // no-cache: `VendorItemUnitsResult` (the union wrapper) has no id, so a cache
    // read can drop its inline-fragment `itemUnits` — same reason the browse
    // hook uses no-cache (see useVendorCatalogBrowse).
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  });

  const mapped = useMemo(
    () => mapVendorItemUnitDetailResponse(data?.vendorItemUnits),
    [data]
  );

  const errorMessage =
    mapped?.kind === 'error' ? mapped.message : (error?.message ?? null);

  return {
    detail: mapped?.kind === 'success' ? mapped.detail : null,
    isLoading: Boolean(unitId) && loading && !mapped,
    errorMessage,
  };
};
